import { useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import session from "../../../SessionHandler/session-helper";
import { removeUser } from "../../../actions";
import { persistor } from "../../../configureStore";
import { currentUser } from "../../../components/Usuario/api-usuario";
import { setUsuario } from "../../../components/Usuario/actions/actions";
import { getQuantNaoLidas } from "../../../components/Mensagem/SalaParticipante/api-sala-participante";
import { setQuantNaoLidas } from "../../../components/Mensagem/SalaParticipante/actions/actions";
import { RESPONSAVEL } from "../../../components/Usuario/Perfil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import SimpleModal from "../Modal/SimpleModal";
import AlunoSelectorMenu from "./AlunoSelectorMenu";
import LateralMenuNav from "./LateralMenuNav";
import UsuarioMenu from "./UsuarioMenu";

const DRAWER_WIDTH = 260;
const DRAWER_OPEN_KEY = "lateralMenuOpen";
const LOGOUT_DELAY = 3000;

const readDrawerOpen = () => {
  try {
    return localStorage.getItem(DRAWER_OPEN_KEY) !== "false";
  } catch {
    return true;
  }
};

export default function LateralMenuSkeleton({ children }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const dispatch = useDispatch();
  const usuario = useSelector((state) => state.usuario);
  const isLogout = useSelector((state) => state.logout);

  // No desktop o menu fica fixo (e lembra se foi recolhido); no celular abre por cima.
  const [desktopOpen, setDesktopOpen] = useState(readDrawerOpen);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [modal, setModal] = useState({ open: false, title: "", description: "" });

  const drawerOpen = isDesktop ? desktopOpen : mobileOpen;
  const usuarioCarregado = Boolean(usuario);

  const setDrawerOpen = (value) => {
    if (isDesktop) {
      setDesktopOpen(value);
      try {
        localStorage.setItem(DRAWER_OPEN_KEY, String(value));
      } catch {
        // Sem armazenamento local: o estado vale só para esta sessão.
      }
    } else {
      setMobileOpen(value);
    }
  };

  useEffect(() => {
    if (usuarioCarregado) return;
    currentUser().then((data) => {
      if (!data) return;
      if (data.error) {
        errorMessage(data.message);
      } else {
        dispatch(setUsuario(data));
      }
    });
  }, [usuarioCarregado, dispatch]);

  useEffect(() => {
    getQuantNaoLidas().then((data) => {
      if (data && !data.error) {
        dispatch(setQuantNaoLidas(data));
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (!isLogout) return undefined;
    setModal({
      open: true,
      title: "Acesso Token Expirado.",
      description: "Seu token de acesso foi expirado. Realizar novamente o login.",
    });
    const timer = setTimeout(() => {
      localStorage.removeItem("token");
      dispatch(removeUser());
      setRedirect(true);
    }, LOGOUT_DELAY);
    return () => clearTimeout(timer);
  }, [isLogout, dispatch]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    dispatch(removeUser());
    persistor.purge();
    setRedirect(true);
  };

  if (redirect) {
    return <Navigate to="/" />;
  }

  const instituicao = usuario?.instituicao;
  const shift = isDesktop && drawerOpen;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <SimpleModal modal={modal} setModal={setModal} />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: instituicao?.corPrimaria || undefined,
          borderBottom: 1,
          borderColor: "rgba(255,255,255,0.12)",
          width: shift ? `calc(100% - ${DRAWER_WIDTH}px)` : "100%",
          ml: shift ? `${DRAWER_WIDTH}px` : 0,
          transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {!shift && (
            <Tooltip title="Abrir menu">
              <IconButton color="inherit" edge="start" onClick={() => setDrawerOpen(true)} className="no-print">
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
          <Box flexGrow={1} minWidth={0}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              noWrap
              sx={{ color: instituicao?.corSecundaria || "inherit", lineHeight: 1.2 }}
            >
              {instituicao?.descricao}
            </Typography>
            <Typography variant="caption" noWrap sx={{ opacity: 0.85 }}>
              Ano letivo {session.getAnoLetivo()}
            </Typography>
          </Box>
          {usuario?.perfil === RESPONSAVEL && <AlunoSelectorMenu />}
          <UsuarioMenu onLogout={handleLogout} />
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isDesktop ? "persistent" : "temporary"}
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        className="no-print"
        sx={{
          width: isDesktop ? DRAWER_WIDTH : undefined,
          flexShrink: 0,
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
        }}
      >
        <LateralMenuNav
          showCloseButton
          onClose={() => setDrawerOpen(false)}
          onNavigate={() => !isDesktop && setMobileOpen(false)}
        />
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: { xs: 1.5, sm: 3 },
          ml: isDesktop && !drawerOpen ? `-${DRAWER_WIDTH}px` : 0,
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
